import { useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth';
import { AccountMenu } from './components/AccountMenu';
import { BrandLockup, PoweredBy } from './components/Brand';
import { TabBar } from './components/TabBar';
import {
  AssignmentsIcon,
  ChecklistsIcon,
  CloseIcon,
  SettingsIcon,
  TemplatesIcon,
  DashboardIcon,
  MoneyIcon,
  PeopleIcon,
  ScheduleIcon,
  TagsIcon,
  UsersIcon,
} from './components/NavIcons';
import { AssignmentForm } from './pages/AssignmentForm';
import { AssignmentsList } from './pages/AssignmentsList';
import { BillDetail } from './pages/BillDetail';
import { BillRun } from './pages/BillRun';
import { ChecklistBuilder } from './pages/ChecklistBuilder';
import { Checklists } from './pages/Checklists';
import { Dashboard } from './pages/Dashboard';
import { InvoiceDetail, InvoicePrint } from './pages/Invoices';
import { Login } from './pages/Login';
import { InvoiceRun } from './pages/InvoiceRun';
import { Money } from './pages/Money';
import { PeopleList } from './pages/PeopleList';
import { PersonForm } from './pages/PersonForm';
import { Schedule } from './pages/Schedule';
import { Settings } from './pages/Settings';
import { ForgotPassword, SetPassword } from './pages/SetPassword';
import { Tags } from './pages/Tags';
import { TemplateEditor } from './pages/TemplateEditor';
import { Templates } from './pages/Templates';
import { Users } from './pages/Users';
import { VisitDetail } from './pages/VisitDetail';
import { useMedia } from './hooks';
import { useTheme } from './theme';

// `tabbar`: on a phone the bottom bar already has it, so the More sheet leaves it out.
const NAV = [
  { to: '/', label: 'Dashboard', end: true, Icon: DashboardIcon, tabbar: true },
  { to: '/assignments', label: 'Assignments', Icon: AssignmentsIcon, tabbar: true },
  { to: '/schedule', label: 'Schedule', Icon: ScheduleIcon, tabbar: true },
  // An invoice's or bill's own page is still part of Money.
  {
    to: '/money',
    label: 'Money',
    Icon: MoneyIcon,
    within: ['/invoices/', '/bills/'],
    tabbar: true,
  },
  { to: '/people', label: 'People', Icon: PeopleIcon },
];
const MORE = [
  { to: '/tags', label: 'Tags', Icon: TagsIcon },
  { to: '/checklists', label: 'Checklists', Icon: ChecklistsIcon },
  { to: '/templates', label: 'Templates', Icon: TemplatesIcon },
  { to: '/users', label: 'Users', Icon: UsersIcon },
];

export function App() {
  const { user, signOut } = useAuth();
  const [menu, setMenu] = useState(false);
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  // A phone gets a bottom bar for the main pages; More opens the same pane the menu button did.
  const phone = useMedia('(max-width: 720px)');
  // On a phone the menu is a slide-out pane; Escape closes it like any dialog.
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenu(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu]);
  // Going anywhere, from the sheet or the tab bar, puts the sheet away.
  useEffect(() => setMenu(false), [location.pathname]);
  if (user === undefined) return <p className="center">Loading…</p>;
  const canManageSettings = user?.capabilities.includes('teknika_manage_settings') ?? false;

  // Emailed links work whether or not anyone is signed in.
  if (user === null) {
    return (
      <main>
        <Routes>
          <Route path="/accept-invite" element={<SetPassword invite />} />
          <Route path="/reset-password" element={<SetPassword invite={false} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/login/:slug" element={<Login />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </main>
    );
  }

  return (
    <div className="shell">
      <a className="skip" href="#main">
        Skip to content
      </a>
      <nav className="side" aria-label="Main">
        <BrandLockup className="brand" />
        <button
          type="button"
          className="menu-toggle quiet"
          aria-label="Menu"
          aria-expanded={menu}
          aria-controls="nav-pane"
          onClick={() => setMenu(!menu)}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
            <path
              d="M4 7h16M4 12h16M4 17h16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        {menu && (
          <div className="pane-backdrop" aria-hidden="true" onClick={() => setMenu(false)} />
        )}
        {/* Beside the page on desktop; on a phone, a pane that slides out from the menu button. */}
        <div
          id="nav-pane"
          className={menu ? 'pane open' : 'pane'}
          role={phone ? 'dialog' : undefined}
          aria-modal={phone && menu ? true : undefined}
          aria-labelledby={phone ? 'nav-pane-title' : undefined}
        >
          {phone && (
            <div className="pane-head">
              <h2 id="nav-pane-title">More</h2>
              <button
                type="button"
                className="quiet icon-button"
                aria-label="Close"
                onClick={() => setMenu(false)}
              >
                <CloseIcon />
              </button>
            </div>
          )}
          <div id="nav-links" className="links" onClick={() => setMenu(false)}>
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  [
                    isActive || n.within?.some((p) => location.pathname.startsWith(p))
                      ? 'active'
                      : '',
                    n.tabbar ? 'in-tabbar' : '',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
              >
                <n.Icon />
                {n.label}
              </NavLink>
            ))}
            <span className="group" aria-hidden="true">
              Manage
            </span>
            {MORE.map((n) => (
              <NavLink key={n.to} to={n.to}>
                <n.Icon />
                {n.label}
              </NavLink>
            ))}
            {/* On a desktop Settings is in the account menu; on a phone it is one tap away here. */}
            {phone && (
              <NavLink to="/settings">
                <SettingsIcon />
                Settings
              </NavLink>
            )}
          </div>
          <div className="side-foot">
            <PoweredBy />
            <AccountMenu
              name={user.displayName}
              theme={theme}
              onTheme={setTheme}
              onSignOut={() => void signOut()}
            />
          </div>
        </div>
      </nav>
      <div className="content">
        <main id="main">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/people" element={<PeopleList />} />
            <Route path="/people/new" element={<PersonForm />} />
            <Route path="/people/:id" element={<PersonForm />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/visits/:id" element={<VisitDetail />} />
            <Route path="/assignments" element={<AssignmentsList />} />
            <Route path="/assignments/new" element={<AssignmentForm />} />
            <Route path="/assignments/:id" element={<AssignmentForm />} />
            <Route path="/money" element={<Money />} />
            <Route path="/money/invoice-run" element={<InvoiceRun />} />
            <Route
              path="/money/invoices/new"
              element={<Navigate to="/money/invoice-run" replace />}
            />
            <Route path="/money/bill-run" element={<BillRun />} />
            <Route path="/money/bills/new" element={<Navigate to="/money/bill-run" replace />} />
            <Route path="/invoices" element={<Navigate to="/money?tab=invoices" replace />} />
            <Route path="/invoices/:id" element={<InvoiceDetail />} />
            <Route path="/invoices/:id/print" element={<InvoicePrint />} />
            <Route path="/bills" element={<Navigate to="/money?tab=bills" replace />} />
            <Route path="/bills/:id" element={<BillDetail />} />
            <Route path="/tags" element={<Tags />} />
            <Route path="/templates" element={<Templates />} />
            <Route path="/templates/:trigger" element={<TemplateEditor />} />
            <Route path="/users" element={<Users canManageOffice={canManageSettings} />} />
            <Route path="/portal-users" element={<Navigate to="/users?tab=portal" replace />} />
            <Route path="/staff" element={<Navigate to="/users?tab=office" replace />} />
            <Route path="/checklists" element={<Checklists />} />
            <Route path="/checklists/new" element={<ChecklistBuilder />} />
            <Route path="/checklists/:id" element={<ChecklistBuilder />} />
            <Route
              path="/deactivated"
              element={<Navigate to="/settings?tab=deactivated" replace />}
            />
            <Route path="/activity" element={<Navigate to="/settings?tab=activity" replace />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
      {phone && <TabBar menuOpen={menu} onMore={() => setMenu(!menu)} />}
    </div>
  );
}
